import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Reveal, SectionLabel } from "@/components/Reveal";
import { brands } from "@/data/brands";
import { Seo } from "@/components/Seo";

const sectorGroups = [
  "Coaching, Training & Consulting",
  "Child Education & Experience",
  "Child Experience",
  "Franchise & Business Consulting",
  "Sports Education",
];

export default function Brands() {
  return (
    <div data-testid="brands-page">
      <Seo
        title="Brands We Represent"
        description="Explore the world-class knowledge brands we represent — ActionCOACH, Brian Tracy Solutions, Network In Action, Sales Geek, Mars Venus Coaching, NLP4Kids, Fish n Flips and more across coaching, education and sports."
        path="/brands"
      />
      {/* HEADER */}
      <section className="bg-navy noise-overlay text-white pt-20 pb-24 md:pt-28 md:pb-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <SectionLabel className="text-gold">Brands We Represent</SectionLabel>
            <h1 className="mt-6 text-4xl md:text-5xl lg:text-6xl font-bold leading-[1.05] max-w-3xl text-balance">
              A portfolio of world-class knowledge brands.
            </h1>
            <p className="mt-6 text-lg md:text-xl text-white/70 max-w-2xl leading-relaxed">
              We have been mandated by leading brands across coaching, education,
              child experience and sports — trusted to bring them to new markets.
            </p>
          </Reveal>
        </div>
      </section>

      {/* GRID */}
      <section className="py-20 md:py-28 bg-canvas">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {brands.map((b, i) => (
              <Reveal key={b.name} delay={(i % 3) * 0.08}>
                <div
                  data-testid={`brand-card-${i}`}
                  className="group bg-white border border-navy/10 h-full flex flex-col hover:-translate-y-1 hover:shadow-xl transition-all duration-300"
                >
                  <div
                    className={`h-40 flex items-center justify-center p-8 border-b border-navy/5 ${
                      b.dark ? "bg-navy" : "bg-canvas-alt"
                    }`}
                  >
                    {b.logo ? (
                      <img src={b.logo} alt={b.name} className="max-h-24 max-w-[80%] object-contain" />
                    ) : (
                      <span className={`text-2xl font-bold font-heading ${b.dark ? "text-white" : "text-navy"}`}>
                        {b.name}
                      </span>
                    )}
                  </div>
                  <div className="p-7 flex flex-col flex-1">
                    <span className="text-xs uppercase tracking-[0.15em] font-semibold text-teal">
                      {b.category}
                    </span>
                    <h3 className="mt-2 text-xl font-semibold text-navy">{b.name}</h3>
                    <p className="mt-3 text-ink-secondary leading-relaxed flex-1">{b.blurb}</p>
                    <span className="mt-4 text-xs font-semibold text-ink-muted uppercase tracking-wider">
                      {b.sector}
                    </span>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-canvas-alt py-20">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <Reveal>
            <h2 className="text-3xl md:text-4xl font-semibold text-navy">
              Want your brand in this portfolio?
            </h2>
            <p className="mt-4 text-lg text-ink-secondary">
              If you run a knowledge-based business ready to franchise, we'd love to talk.
            </p>
            <Link
              to="/contact"
              data-testid="brands-cta"
              className="mt-8 inline-flex items-center gap-2 bg-navy text-white px-10 py-4 font-semibold hover:bg-navy-light transition-colors group"
            >
              Partner With Us
              <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
